// CallTimerService.js
// Create this file in your services folder (e.g., src/services/CallTimerService.js)

class CallTimerService {
  constructor(firestoreService) {
    this.firestoreService = firestoreService;
    this.timerInterval = null;
    this.currentTimer = null;
    this.onTimerUpdate = null;
    this.onTimerExpired = null;
    this.isInitialized = false;
  }

  // FIXED: Initialize timer from Firebase with proper state handling
  async initializeTimer() {
    try {
      console.log('⏰ Initializing call timer...');
      this.currentTimer = await this.firestoreService.getCallTimer();
      this.isInitialized = true;
      
      console.log('✅ Timer initialized:', this.currentTimer);
      
      // FIXED: If timer was active when user left, handle elapsed time properly
      if (this.currentTimer.isActive && this.currentTimer.sessionStartTime) {
        const now = new Date();
        const sessionStart = this.currentTimer.sessionStartTime.toDate ? 
          this.currentTimer.sessionStartTime.toDate() : 
          new Date(this.currentTimer.sessionStartTime);
        const elapsedSeconds = Math.floor((now - sessionStart) / 1000);
        
        // FIXED: Don't modify remaining time during initialization
        // The remaining time should already be accurate from the database
        console.log(`⏰ Timer was active: ${elapsedSeconds}s elapsed, ${this.currentTimer.remainingTime}s remaining`);
        
        if (this.currentTimer.remainingTime <= 0) {
          console.log('⏰ Timer expired while user was away');
          await this.handleTimerExpired();
        } else {
          // Timer is still valid, just log the state
          console.log(`⏰ Timer resumed with ${this.currentTimer.remainingTime}s remaining`);
        }
      }
      
      return this.currentTimer;
    } catch (error) {
      console.error('❌ Failed to initialize timer:', error);
      // Fallback to default timer
      this.currentTimer = {
        totalTime: 1200,
        remainingTime: 1200,
        lastPaused: null,
        isActive: false,
        sessionStartTime: null,
        resetCount: 0,
        lastResetBy: null,
        lastResetAt: null
      };
      return this.currentTimer;
    }
  }

  // Start the timer
  async startTimer() {
    if (!this.isInitialized) {
      await this.initializeTimer();
    }

    if (this.currentTimer.remainingTime <= 0) {
      console.log('⏰ Cannot start - timer expired');
      if (this.onTimerExpired) this.onTimerExpired();
      return false;
    }

    console.log('⏰ Starting timer with', this.currentTimer.remainingTime, 'seconds remaining');
    
    this.currentTimer.isActive = true;
    this.currentTimer.sessionStartTime = new Date().toISOString();
    this.currentTimer.lastPaused = null;
    
    await this.saveTimer();

    // Clear any existing interval
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    // Start countdown
    this.timerInterval = setInterval(async () => {
      if (this.currentTimer.remainingTime > 0) {
        this.currentTimer.remainingTime--;
        
        // Update callback for UI
        if (this.onTimerUpdate) {
          this.onTimerUpdate(this.currentTimer.remainingTime);
        }
        
        // Save to Firebase every 10 seconds to reduce writes
        if (this.currentTimer.remainingTime % 10 === 0) {
          await this.saveTimer();
        }
        
        // Check if expired
        if (this.currentTimer.remainingTime <= 0) {
          await this.handleTimerExpired();
        }
      }
    }, 1000);

    return true;
  }

  // Pause the timer
  async pauseTimer() {
    if (!this.currentTimer.isActive) return;

    console.log('⏸️ Pausing timer at', this.currentTimer.remainingTime, 'seconds');
    
    this.currentTimer.isActive = false;
    this.currentTimer.lastPaused = new Date().toISOString();
    this.currentTimer.sessionStartTime = null;
    
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    
    await this.saveTimer();
  }

  // Reset timer (user or admin)
  async resetTimer(adminUserId = null) {
    console.log('🔄 Resetting timer...', adminUserId ? `by admin: ${adminUserId}` : 'by user');
    
    // Stop current timer
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    
    // Reset timer data
    this.currentTimer = await this.firestoreService.resetCallTimer(adminUserId);
    
    // Update UI
    if (this.onTimerUpdate) {
      this.onTimerUpdate(this.currentTimer.remainingTime);
    }
    
    console.log('✅ Timer reset to', this.currentTimer.remainingTime, 'seconds');
    return this.currentTimer;
  }

  // Handle timer expiration
  async handleTimerExpired() {
    console.log('⏰ Timer expired!');
    
    this.currentTimer.isActive = false;
    this.currentTimer.remainingTime = 0;
    this.currentTimer.sessionStartTime = null;
    
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    
    await this.saveTimer();
    
    if (this.onTimerExpired) {
      this.onTimerExpired();
    }
  }

  // FIXED: Save timer state to Firebase with proper error handling
  async saveTimer() {
    try {
      await this.firestoreService.updateCallTimer(this.currentTimer);
      console.log('⏰ Timer state saved:', this.currentTimer.remainingTime, 'seconds remaining');
    } catch (error) {
      console.error('❌ Failed to save timer:', error);
    }
  }

  // Get current timer state
  getTimerState() {
    return this.currentTimer;
  }

  // Set callbacks
  setTimerUpdateCallback(callback) {
    this.onTimerUpdate = callback;
  }

  setTimerExpiredCallback(callback) {
    this.onTimerExpired = callback;
  }

  // Admin function to reset any user's timer
  async adminResetUserTimer(targetUserId, adminUserId) {
    try {
      const resetData = await this.firestoreService.adminResetUserTimer(targetUserId, adminUserId);
      console.log('🔄 Admin reset successful for user:', targetUserId);
      return resetData;
    } catch (error) {
      console.error('❌ Admin reset failed:', error);
      throw error;
    }
  }

  // FIXED: Cleanup with proper timer pause
  cleanup() {
    console.log('🧹 Cleaning up CallTimerService...');
    
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
    
    // FIXED: Pause timer when cleaning up to preserve remaining time
    if (this.currentTimer && this.currentTimer.isActive) {
      // Don't await this to avoid blocking cleanup
      this.pauseTimer().catch(error => {
        console.error('Failed to pause timer during cleanup:', error);
      });
    }
    
    console.log('✅ CallTimerService cleanup completed');
  }

  // Get formatted time string
  formatTime(seconds) {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  // Check if timer is expired
  isExpired() {
    return this.currentTimer && this.currentTimer.remainingTime <= 0;
  }

  // Check if timer is active
  isActive() {
    return this.currentTimer && this.currentTimer.isActive;
  }

  // Get remaining time in different formats
  getRemainingTime() {
    return this.currentTimer ? this.currentTimer.remainingTime : 1200;
  }

  getRemainingTimeFormatted() {
    return this.formatTime(this.getRemainingTime());
  }

  // Get timer status for UI
  getTimerStatus() {
    const remaining = this.getRemainingTime();
    
    if (remaining <= 0) return 'expired';
    if (remaining <= 30) return 'critical';
    if (remaining <= 60) return 'warning';
    return 'normal';
  }

  // FIXED: Force sync with Firebase with proper state update
  async forceSync() {
    if (!this.isInitialized) return;
    
    try {
      const latestTimer = await this.firestoreService.getCallTimer();
      
      // FIXED: Only update if timer is not currently active to avoid conflicts
      if (!this.currentTimer?.isActive) {
        this.currentTimer = latestTimer;
        
        if (this.onTimerUpdate) {
          this.onTimerUpdate(this.currentTimer.remainingTime);
        }
        
        console.log('🔄 Timer synced from Firebase:', latestTimer.remainingTime, 'seconds');
      } else {
        console.log('⏰ Skipping sync - timer is active');
      }
      
      return latestTimer;
    } catch (error) {
      console.error('❌ Failed to sync timer:', error);
    }
  }

  // Add time (admin only - for testing or special cases)
  async addTime(seconds, adminUserId) {
    if (!this.currentTimer) return false;
    
    try {
      const newRemainingTime = Math.min(
        this.currentTimer.totalTime, // Don't exceed total time
        this.currentTimer.remainingTime + seconds
      );
      
      this.currentTimer.remainingTime = newRemainingTime;
      this.currentTimer.lastResetBy = adminUserId;
      this.currentTimer.lastResetAt = new Date().toISOString();
      
      await this.saveTimer();
      
      if (this.onTimerUpdate) {
        this.onTimerUpdate(this.currentTimer.remainingTime);
      }
      
      console.log(`⏰ Added ${seconds}s to timer by admin: ${adminUserId}`);
      return true;
    } catch (error) {
      console.error('❌ Failed to add time:', error);
      return false;
    }
  }

  // FIXED: Get accurate remaining time accounting for active sessions
  getRealTimeRemainingTime() {
    if (!this.currentTimer) return 0;
    
    // If timer is not active, return stored remaining time
    if (!this.currentTimer.isActive || !this.currentTimer.sessionStartTime) {
      return this.currentTimer.remainingTime;
    }
    
    // If timer is active, calculate real-time remaining time
    const now = new Date();
    const sessionStart = new Date(this.currentTimer.sessionStartTime);
    const elapsedSeconds = Math.floor((now - sessionStart) / 1000);
    const realTimeRemaining = Math.max(0, this.currentTimer.remainingTime - elapsedSeconds);
    
    return realTimeRemaining;
  }

  // FIXED: Get real-time formatted remaining time
  getRealTimeRemainingTimeFormatted() {
    return this.formatTime(this.getRealTimeRemainingTime());
  }
}

export default CallTimerService;